<template>
  <div class="uf" :style="{ '--tc': side.bright }">
    <div class="uf-top">
      <UnitSprite :unit="unit" :team-color="side.pal" :fallback-src="fallback" :size="72" />
      <div class="uf-id">
        <div class="uf-name">
          <span v-if="unit.RaidBoss" class="uf-boss" v-tip="'Raid boss'">★</span><b :style="nameColor ? { color: nameColor } : null">{{ unit.Name }}</b>
          <small v-if="unit.Npc" class="lx-npc" v-tip="NPC_TIP">NPC</small>
          <span class="lx-grade uf-grade" :style="{ background: grade.color }" v-tip="'Grade ' + grade.grade + ' · expected win rate ~' + grade.score + '%'">{{ grade.grade }}</span>
        </div>
        <div class="uf-sub">{{ unit.Gender === 'Monster' ? '' : unit.Gender + ' ' }}{{ unit.Class }} · {{ unit.Sign }}</div>
        <div v-if="zodiac.length" class="uf-zod">
          <span v-for="z in zodiac" :key="z.name" class="uf-z" :class="z.c.toLowerCase()" v-tip="z.c + ' zodiac against ' + z.name">{{ z.c === 'Best' || z.c === 'Good' ? '▲' : '▼' }} {{ z.name }}</span>
        </div>
      </div>
      <EvadeGrid :ev="info.evade" :sources="info.sources" />
    </div>

    <div class="uf-stats">
      <span v-for="s in statTiles" :key="s.k" v-tip="s.tip"><i>{{ s.k }}</i><b>{{ s.v }}</b></span>
    </div>
    <div v-if="drivers.up.length || drivers.down.length" class="uf-drivers">
      <span v-for="p in drivers.up" :key="'u' + p.label" class="uf-chip up">{{ p.label }} +{{ p.contribution }}</span>
      <span v-for="p in drivers.down" :key="'d' + p.label" class="uf-chip down">{{ p.label }} {{ p.contribution }}</span>
    </div>

    <div class="uf-body">
      <section class="uf-sec">
        <h4>Gear</h4>
        <div v-for="g in gear" :key="g.slot" class="uf-gear">
          <img v-if="g.name" :src="g.icon" :alt="''" @error="$event.target.style.visibility = 'hidden'"><span v-else class="uf-empty"></span>
          <span class="uf-gear-main">
            <b :style="g.name ? wrStyleFor(unit, g.dim, g.name) : null" v-tip="g.name ? wrTipFor(unit, g.dim, g.name) : ''">{{ g.name || 'Unarmed' }}<em v-if="g.hit"> {{ g.hit }}</em></b>
            <i v-if="g.line">{{ g.line }}</i>
          </span>
        </div>
        <div v-if="elements.length" class="uf-els">
          <span v-for="e in elements" :key="e.k + e.el" class="uf-el" :class="e.k">{{ e.label }} {{ e.el }}</span>
        </div>
      </section>
      <section class="uf-sec">
        <h4>Skills <small>vs a 60-Faith target</small></h4>
        <div v-for="set in sets" :key="set.title" class="uf-set">
          <div class="uf-set-title">{{ set.title }}</div>
          <div class="uf-skills">
            <span v-for="sk in set.skills" :key="sk.name" class="uf-sk" v-tip="[sk.beast ? 'Needs a Beastmaster ally' : '', sk.tier ? sk.tier.label + ' · rolls ' + sk.roll + '%' : '', wrTipFor(unit, 'Ability', sk.name)].filter(Boolean).join(' · ')">
              <i v-if="sk.tier" class="uf-rar" :style="{ background: sk.tier.color }">{{ sk.roll }}%</i>
              <span :style="wrStyleFor(unit, 'Ability', sk.base)">{{ sk.name }}</span>
              <em v-if="sk.out">{{ sk.out }}</em>
            </span>
          </div>
        </div>
        <div class="uf-rsm">
          <span v-for="r in rsm" :key="r.k"><i>{{ r.k }}</i><b :style="r.v ? wrStyleFor(unit, r.dim, r.v) : null" v-tip="r.v ? wrTipFor(unit, r.dim, r.v) : ''">{{ r.v || '—' }}</b></span>
        </div>
        <div v-if="innates.length" class="uf-innate">Innate: {{ innates.join(' · ') }}</div>
      </section>
    </div>
    <button class="uf-more" @click="$emit('open')">Full card · vs {{ foe ? foe.label : 'the other team' }} ›</button>
  </div>
</template>

<script>
// A unit's full card laid out inline, for the team section's "full cards" mode (people missed the old site's "expand
// cards by default"): identity, grade and what drives it, zodiac against the team it faces, evasion, stats, gear with
// what each piece adds, every skill with its output against a 60-Faith target (lib/unitOutput, as the old cards
// showed "Koutetsu (100)") and its roll rarity, and reaction / support / move. Win-rate colors follow the team bar's
// switch. A tall card, four abreast in its team's row.
import UnitSprite from '../UnitSprite.vue';
import EvadeGrid from '../spotlight/EvadeGrid.vue';
import { unitInfo, gearRows, elementChips, skillSets } from '../spotlight/unitInfo.js';
import { unitOutput } from '../../lib/unitOutput.js';
import { fmtStat, endlessTip } from '../../lib/statDisplay.js';
import { unitGrade, topParts } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { zodiacCompat } from '../../lib/zodiac.js';
import { wrStyleFor, wrTipFor } from '../../lib/wrPref.js';
import { NPC_TIP } from '../../lib/fftbgLive.js';

const GEAR_DIM = { Mainhand: 'Weapon', Offhand: 'Weapon', Head: 'Head', Armor: 'Armor', Accessory: 'Accessory' };

export default {
  name: 'UnitFullPanel',
  components: { UnitSprite, EvadeGrid },
  props: {
    unit: { type: Object, required: true },
    side: { type: Object, required: true },
    foe: { type: Object, default: null },
    gd: { type: Object, required: true },
    fallback: { type: String, default: '' },
    nameColor: { type: String, default: null },
  },
  emits: ['open'],
  data() { return { NPC_TIP }; },
  computed: {
    info() { return unitInfo(this.unit, this.gd); },
    out() { try { return unitOutput(this.unit, this.gd); } catch (e) { return { weapons: [], abilities: [] }; } },
    grade() { return unitGrade(this.unit, rosterStats, this.gd); },
    drivers() { return topParts(this.grade.parts || [], 2); },
    statTiles() {
      const s = this.info.stats, u = this.unit;
      return [['HP', 'hp'], ['MP', 'mp'], ['Spd', 'speed'], ['Mv', 'move'], ['Jp', 'jump'], ['PA', 'pa'], ['MA', 'ma']].map(([k, f]) => ({ k, v: fmtStat(f, s[f]), tip: endlessTip(f, s[f], u) }))
        .concat([{ k: 'Br', v: u.Brave }, { k: 'Fa', v: u.Faith }]);
    },
    zodiac() {
      if (!this.foe) return [];
      return this.foe.units.map((f) => ({ name: f.Name, c: zodiacCompat(this.unit.Gender, this.unit.Sign, f.Gender, f.Sign) })).filter((z) => z.c && z.c !== 'Neutral');
    },
    // each weapon carries its basic-attack damage, as the old card's "Ice Brand (144)"
    gear() {
      const hits = {}; for (const w of this.out.weapons) hits[w.name] = w.out;
      return gearRows(this.unit, this.gd).map((g) => ({ ...g, dim: GEAR_DIM[g.key], hit: g.key === 'Mainhand' || g.key === 'Offhand' ? hits[g.name || 'Unarmed'] || '' : '' }))
        .filter((g) => !(this.unit.Gender === 'Monster' && !g.name));
    },
    elements() { return elementChips(this.info.elements); },
    sets() {
      const outs = {}; for (const a of this.out.abilities) outs[a.name] = a.out;
      return skillSets(this.unit, this.gd).map((s) => ({ ...s, skills: s.skills.map((k) => { const base = k.name.replace(/ ★$/, ''); return { ...k, base, out: outs[base] || '' }; }) }));
    },
    rsm() { const u = this.unit; return [{ k: 'Reaction', v: u.ReactionSkill, dim: 'ReactionSkill' }, { k: 'Support', v: u.SupportSkill, dim: 'SupportSkill' }, { k: 'Move', v: u.MoveSkill, dim: 'MoveSkill' }]; },
    innates() { return ((this.info.classBase && this.info.classBase.innates) || []).map((i) => i && i.name).filter(Boolean); },
  },
  methods: { wrStyleFor, wrTipFor },
};
</script>

<style scoped>
.uf { padding: 10px; color: var(--ink); text-align: left; border: 1px solid var(--brass-edge); border-top: 3px solid var(--tc); border-radius: 4px; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); display: flex; flex-direction: column; }
.uf.boss { box-shadow: inset 3px 0 0 gold; }
.uf-top { display: flex; gap: 8px; align-items: flex-start; }
.uf-id { flex: 1; min-width: 0; }
.uf-name { display: flex; align-items: center; gap: 6px; min-width: 0; }
.uf-name b { font-family: var(--cond); font-weight: 700; font-size: 17px; color: var(--ink-hi); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.uf-boss { color: gold; }
.uf-grade { font-size: 13px; }
.uf-sub { font-size: 12.5px; color: var(--mute); }
.uf-zod { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 3px; }
.uf-z { padding: 0 6px; border-radius: 8px; font-size: 11px; }
.uf-z.best, .uf-z.good { background: rgba(80,170,90,.2); color: #9fe0a6; }
.uf-z.bad, .uf-z.worst { background: rgba(200,80,70,.2); color: #f0a49b; }
.uf-stats { display: grid; grid-template-columns: repeat(9, minmax(0, 1fr)); gap: 1px; margin-top: 6px; background: rgba(217,180,94,.22); border: 1px solid rgba(217,180,94,.22); border-radius: 3px; overflow: hidden; }
.uf-stats span { display: flex; flex-direction: column; align-items: center; padding: 2px 0; background: #121826; }
.uf-stats i { font-style: normal; font-family: var(--cond); font-weight: 600; font-size: 10.5px; letter-spacing: .6px; text-transform: uppercase; color: var(--brass); }
.uf-stats b { font-family: var(--cond); font-weight: 700; font-size: 16px; color: var(--ink-hi); font-variant-numeric: tabular-nums; }
.uf-drivers { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 5px; }
.uf-chip { padding: 0 7px; border-radius: 9px; font-size: 11.5px; }
.uf-chip.up { background: rgba(80,170,90,.14); color: #9fe0a6; } .uf-chip.down { background: rgba(200,80,70,.14); color: #f0a49b; }
.uf-body { display: grid; grid-template-columns: minmax(0, 1fr); gap: 6px; flex: 1; align-content: start; margin-top: 6px; }
.uf-sec h4 { margin: 0 0 3px; font-family: var(--cond); font-weight: 700; font-size: 11.5px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--brass); border-bottom: 1px solid rgba(217,180,94,.25); }
.uf-sec h4 small { font-weight: 500; letter-spacing: .3px; text-transform: none; color: var(--mute); margin-left: 4px; }
.uf-gear { display: flex; gap: 6px; align-items: flex-start; padding: 1px 0; }
.uf-gear img, .uf-empty { width: 20px; height: 20px; image-rendering: pixelated; flex: none; margin-top: 1px; }
.uf-gear-main { display: flex; flex-direction: column; min-width: 0; }
.uf-gear-main b { font-family: var(--cond); font-weight: 600; font-size: 14px; color: var(--ink-hi); }
.uf-gear-main b em { margin-left: 5px; }
.uf-gear-main b em, .uf-sk em { font-style: normal; color: #f3d68e; font-variant-numeric: tabular-nums; }
.uf-gear-main i { font-style: normal; font-size: 11px; color: var(--mute); }
.uf-els { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 4px; }
.uf-el { padding: 0 7px; border-radius: 9px; font-size: 11px; background: rgba(255,255,255,.08); }
.uf-el.absorb, .uf-el.cancel, .uf-el.half { color: #9fe0a6; } .uf-el.weak { color: #f0a49b; } .uf-el.strengthen { color: #f3d68e; }
.uf-set { margin-bottom: 4px; }
.uf-set-title { font-family: var(--cond); font-weight: 600; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--mute); }
.uf-skills { display: flex; flex-direction: column; }
.uf-sk { display: flex; align-items: baseline; gap: 5px; font-size: 13px; color: var(--ink-hi); line-height: 1.35; }
.uf-sk em { margin-left: auto; padding-left: 6px; font-size: 12.5px; white-space: nowrap; }
.uf-rar { font-style: normal; padding: 0 4px; border-radius: 3px; font-size: 10px; line-height: 14px; color: #fff; text-shadow: 0 1px 0 rgba(0,0,0,.5); }
.uf-rsm { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; margin-top: 4px; }
.uf-rsm i { display: block; font-style: normal; font-family: var(--cond); font-weight: 600; font-size: 10.5px; letter-spacing: .8px; text-transform: uppercase; color: var(--brass); }
.uf-rsm b { font-weight: 500; font-size: 12.5px; color: var(--ink-hi); }
.uf-innate { margin-top: 3px; font-size: 11px; color: var(--mute); }
.uf-more { align-self: flex-start; margin-top: 6px; padding: 1px 8px; border: 1px solid #7a5f2e; border-radius: 3px; background: none; cursor: pointer; font-family: var(--cond); font-weight: 600; font-size: 12px; letter-spacing: .5px; text-transform: uppercase; color: var(--brass); }
.uf-more:hover { background: rgba(217,180,94,.12); }
</style>
